<script setup lang="ts">
import { ref, computed, watch } from "vue";
import InputPrice from "@/components/ui/InputPrice.vue";
import { PriceDisplayModeEnum } from "@/types/enums";

type Amount = string | number | null | undefined;

interface TaxRate {
  id: number;
  name: string;
  amount: number;
}

const props = withDefaults(defineProps<{
  name?: string;
  taxRates: TaxRate[];
  currency?: string;
  valuePrice?: Amount;
  valueOriginalPrice?: Amount;
  taxRateSelectedId?: string | number;
  locale?: string;
  priceDisplayMode?: PriceDisplayModeEnum;
}>(), {
  priceDisplayMode: PriceDisplayModeEnum.TAX_INCLUDED,
});

const isTaxExcluded = computed(() => props.priceDisplayMode === PriceDisplayModeEnum.TAX_EXCLUDED);

const selectedTaxRateAmount = computed(() => {
  return props.taxRates.find(
    (taxRate) => taxRate.id === parseInt(String(selectedTaxRate.value))
  );
});

const withTax = (value: Amount): number | undefined => {
  if (!value) {
    return undefined;
  }
  if (isTaxExcluded.value || !selectedTaxRateAmount.value) {
    return Number(value);
  }
  return Number(value) * (1 + selectedTaxRateAmount.value.amount / 100);
};
const withoutTax = (value: number | null | undefined): string | number | null | undefined => {
  if (!value) {
    return value;
  }
  if (isTaxExcluded.value || !selectedTaxRateAmount.value) {
    return value;
  }
  return (value / (1 + selectedTaxRateAmount.value.amount / 100))
    .toPrecision(6);
};

// The field is rounded for display; keep the stored value untouched until the admin actually changes it.
const valueToSubmit = (inputValue: number | null | undefined, storedValue: Amount): string | number | null | undefined => {
  if (isTaxExcluded.value && storedValue && inputValue != null && Math.abs(inputValue - Number(storedValue)) < 0.005) {
    return storedValue;
  }
  return withoutTax(inputValue);
};

const inputName = ref(props.name);
const selectedTaxRate = ref(props.taxRateSelectedId);
const taxRateSelected = ref(props.taxRateSelectedId);
const inputPrice = ref(withTax(props.valuePrice));
const inputOriginalPrice = ref(withTax(props.valueOriginalPrice));


const getTaxRateSelected = () => {
  taxRateSelected.value = selectedTaxRate.value;
};

watch(
  () => props.valuePrice,
  (newValue) => {
    inputPrice.value = withTax(newValue);
  }
);

watch(
  () => props.valueOriginalPrice,
  (newValue) => {
    inputOriginalPrice.value = withTax(newValue);
  }
);
</script>
<template>
  <div>
    <div class="form-group">
      <label for="activity_tax_rate">
        {{ $t('Tax rate') }}
      </label>
      <select
        @change="getTaxRateSelected"
        v-model="selectedTaxRate"
        class="form-control"
      >
        <option value="">-- {{ $t('Select a tax rate') }} --</option>
        <option
          v-for="taxRate in taxRates"
          :key="taxRate.id"
          :value="taxRate.id"
        >
          {{ taxRate.name }} : {{ $t('rate') }} {{ taxRate.amount }} %
        </option>
      </select>

      <input
        :name="inputName"
        type="hidden"
        v-model="taxRateSelected"
      />
    </div>

    <div class="form-group">
      <label>{{ $t('Current sale price') }} ({{ isTaxExcluded ? $t('Tax excl.') : $t('Tax inc.') }})</label>
      <InputPrice
        :currency="currency"
        :locale="locale"
        v-model="inputPrice"
        :precision="2"
        class="mb-0"
      />
      <input type="hidden" name="price" :value="valueToSubmit(inputPrice, valuePrice)">
    </div>

    <div class="form-group">
      <label>{{ $t('Original Price') }} ({{ isTaxExcluded ? $t('Tax excl.') : $t('Tax inc.') }})</label>
      <InputPrice
        :currency="currency"
        :locale="locale"
        v-model="inputOriginalPrice"
      />
      <input type="hidden" name="original_price" :value="valueToSubmit(inputOriginalPrice, valueOriginalPrice)">
      <small class="form-text text-muted">
        {{ $t('If this price is set, it will appear as a “strikethrough price”. The “Current sale price” will always be applied in the cart.') }}
      </small>
    </div>

  </div>
</template>